import React, { useState, useEffect, useCallback } from "react";
import { getContracts, createContract, updateContract, deleteContract, previewPlan, generatePlan, getAutoPlan, setAutoPlan, getTeam, getProspects } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Plus, Repeat, Pencil, Trash2, Clock, MapPin, Users } from "lucide-react";
import ContractForm, { FREQ } from "./contracts/ContractForm";
import PlanGenerator from "./contracts/PlanGenerator";
import { usePlanPolling } from "./contracts/usePlanPolling";
import ServiceBadge from "./ServiceBadge";
import { log } from "../../lib/log";
import { joinAddress } from "../../lib/address";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const nextMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const ContractCard = ({ c, onEdit, onRemove }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-4 ${c.active ? "" : "opacity-60"}`} data-testid={`contract-card-${c.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-neutral-900">{c.client_nom}</p>
          {c.service && c.service !== "clean" && <ServiceBadge service={c.service} testid={`contract-service-${c.id}`} />}
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>{c.active ? "Actif" : "Inactif"}</span>
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">{FREQ[c.frequence] || c.frequence}</span>
        </div>
        <p className="text-sm text-neutral-500 flex items-center gap-1 mt-0.5"><MapPin size={13} /> {joinAddress(c.adresse, c.ville)}</p>
        <p className="text-sm text-neutral-600 mt-1 flex flex-wrap items-center gap-x-3">
          <span className="flex items-center gap-1"><Clock size={13} /> {c.frequence === "quotidien" ? "Lun–Ven" : (c.jours || []).map((i) => DAYS[i]).join(", ")} à {c.heure} · {c.duree_h} h</span>
          {c.collaborateurs?.length > 0 && <span className="flex items-center gap-1"><Users size={13} /> {c.collaborateurs.map((x) => x.nom).join(", ")}</span>}
        </p>
        {c.prestations?.length > 0 && <p className="text-xs text-neutral-500 mt-1">{c.prestations.join(" · ")}</p>}
        <p className="text-xs text-neutral-400 mt-1">{c.interventions_count || 0} intervention(s) générée(s)</p>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onEdit(c)} data-testid={`contract-edit-${c.id}`} className="text-neutral-500 hover:text-brand p-1.5"><Pencil size={16} /></button>
        <button onClick={() => onRemove(c.id)} data-testid={`contract-delete-${c.id}`} className="text-neutral-400 hover:text-red-500 p-1.5"><Trash2 size={16} /></button>
      </div>
    </div>
  </div>
);

const ContractsPanel = ({ isMaster }) => {
  const [contracts, setContracts] = useState([]);
  const [team, setTeam] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(nextMonth());
  const [preview, setPreview] = useState(null);
  const [reports, setReports] = useState({});
  const [auto, setAuto] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [c, t, p, a] = await Promise.all([getContracts(), getTeam().catch(() => ({ data: { users: [] } })), getProspects(), getAutoPlan()]);
      setContracts(c.data); setTeam((t.data.users || []).filter((u) => u.active !== false)); setProspects(p.data); setAuto(a.data);
    } catch (err) {
      log.error("Chargement clients récurrents :", err);
      toast({ title: "Chargement impossible", variant: "destructive" });
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setReports({}); previewPlan(month).then((r) => setPreview(r.data)).catch(() => setPreview(null)); }, [month, contracts]);
  usePlanPolling(auto?.running, setAuto, load, toast);

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing?.id) await updateContract(editing.id, data); else await createContract(data);
      setEditing(null); load();
      toast({ title: "Fiche client enregistrée ✓" });
    } catch (err) {
      toast({ title: "Erreur", description: err?.response?.data?.detail || "Enregistrement impossible", variant: "destructive" });
    } finally { setSaving(false); }
  };
  const remove = async (id) => { if (window.confirm("Supprimer cette fiche client ? (les interventions déjà générées sont conservées)")) { await deleteContract(id); load(); } };
  const generate = async () => {
    try {
      const r = await generatePlan(month, null, reports);
      toast({ title: "Génération lancée", description: `${r.data.total} créneau(x) à planifier pour ${month} (synchro Dolibarr en cours)…` });
      setAuto((a) => ({ ...a, running: true, progress: 0, total: r.data.total }));
    } catch (err) {
      toast({ title: "Impossible", description: err?.response?.data?.detail || "Erreur", variant: "destructive" });
    }
  };
  const toggleAuto = async (patch) => { const r = await setAutoPlan({ ...auto, ...patch }); setAuto(r.data); toast({ title: "Planification automatique mise à jour ✓" }); };

  return (
    <div data-testid="contracts-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div className="flex items-center gap-2"><Repeat size={20} className="text-brand" /><h2 className="font-heading font-bold text-xl text-neutral-900">Clients récurrents & planification</h2></div>
        <button onClick={() => setEditing({})} data-testid="contract-new-btn" className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-md"><Plus size={16} /> Nouvelle fiche client</button>
      </div>
      <p className="text-sm text-neutral-500 mb-4">Renseignez pour chaque client ses jours, horaires, fréquence, prestations et easers : les interventions du mois sont générées automatiquement (agenda CRM + Dolibarr, GPS, rapports).</p>

      <PlanGenerator month={month} setMonth={setMonth} preview={preview} reports={reports} setReports={setReports} auto={auto} setAuto={setAuto} toggleAuto={toggleAuto} generate={generate} isMaster={isMaster} />

      {contracts.length === 0 ? (
        <p className="text-neutral-500 text-sm text-center py-10">Aucune fiche client récurrent. Créez-en une pour lancer la planification automatique.</p>
      ) : (
        <div className="space-y-3">{contracts.map((c) => <ContractCard key={c.id} c={c} onEdit={setEditing} onRemove={remove} />)}</div>
      )}

      {editing && <ContractForm initial={editing} team={team} prospects={prospects} onSubmit={save} onClose={() => setEditing(null)} loading={saving} />}
    </div>
  );
};

export default ContractsPanel;
